import { memo, type ReactNode, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Command, useCommandState } from '@air/cmdk';
import { cn } from '@air/utils-tailwind';

import { SearchCommandShortcutsFooter } from '~/components/SearchCommand/SearchCommandShortcutsFooter';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SearchCommandListFrameProps = {
  children: ReactNode;
};

export const SearchCommandListFrame = memo(({ children }: SearchCommandListFrameProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const search = useCommandState((state) => state.search);
  const listRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;

    const scrollSuggestionsToTop = () => {
      list?.scrollTo({ top: 0 });
    };

    scrollSuggestionsToTop();
    list?.setAttribute('data-query', search);

    // cmdk's parent layout effect then scrollIntoView's the stale selected row.
    queueMicrotask(scrollSuggestionsToTop);
    const frame = requestAnimationFrame(scrollSuggestionsToTop);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [search]);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col',
        ui2026
          ? // Prototype suggestions column is 480px, and also cannot exceed leftover viewport below the field.
            'max-h-[min(480px,calc(100vh-8rem))] grow overflow-hidden ads:border-ads-gray-5 border-gray-5 border-t'
          : 'grow overflow-y-auto rounded-8 border ads:border-strong border-gray-6 ads:bg-base bg-gray-1 shadow-search-command',
      )}
      data-testid="SEARCH_COMMAND_LIST_FRAME"
    >
      <Command.List
        className={cn(
          // Prepending "Search for …" must not keep the previous viewport via overflow anchoring.
          'flex min-h-0 flex-col gap-0 overflow-y-auto [overflow-anchor:none]',
          ui2026 ? 'flex-1 px-3 py-2' : 'px-4 py-3',
        )}
        data-testid="SEARCH_COMMAND_LIST"
        ref={listRef}
      >
        {children}
      </Command.List>
      {ui2026 ? <SearchCommandShortcutsFooter /> : null}
    </div>
  );
});

SearchCommandListFrame.displayName = 'SearchCommandListFrame';
